import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Loader2, ArrowLeft, Plus, Trash2, Pencil, CheckCircle2,
  Circle, MessageSquare, Calendar, AlertOctagon, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { FurnitureNavBar } from '@/components/furniture/FurnitureNavBar';
import {
  adminListStaff, adminListTasks, adminCreateTask, adminUpdateTask, adminDeleteTask,
  adminPostTaskMessage, adminMarkTaskRead, fmtErr,
} from '@/lib/staffApi';
import { getFurnitureProjects } from '@/lib/api';

const PRIORITY_LABEL = {
  1: 'P1 · ASAP',
  2: 'P2 · Deadline',
  3: 'P3 · Do on date',
  4: 'P4 · Within range',
  5: 'P5 · When free',
};
const PRIORITY_TONE = {
  1: 'bg-red-100 text-red-800 border-red-200',
  2: 'bg-orange-100 text-orange-800 border-orange-200',
  3: 'bg-amber-100 text-amber-800 border-amber-200',
  4: 'bg-sky-100 text-sky-800 border-sky-200',
  5: 'bg-muted text-muted-foreground border-border',
};

function fmtDateTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}
function fmtDate(s) {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
function priorityDateLine(t) {
  if (t.priority === 2 && t.due_at) return `Due ${fmtDateTime(t.due_at)}`;
  if (t.priority === 3 && t.due_date) return `Do on ${fmtDate(t.due_date)}`;
  if (t.priority === 4 && (t.start_date || t.end_date)) {
    return `Window ${fmtDate(t.start_date)} – ${fmtDate(t.end_date)}`;
  }
  return '';
}

export default function AdminStaffTasksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const adminPass = typeof window !== 'undefined' ? sessionStorage.getItem('app_password') || '' : '';
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState({ open: [], done: [] });
  const [staff, setStaff] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);  // task object
  const [selected, setSelected] = useState(null); // task open in drawer
  // Capture a deep-linked project_id (`?project=…`) so the create dialog
  // can preselect it. We don't use it as a *list filter* — admins want to
  // see all tasks even when arriving from a specific project tab.
  const prefillProjectId = searchParams.get('project') || '';

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s, p] = await Promise.all([
        adminListTasks(adminPass),
        adminListStaff(adminPass),
        getFurnitureProjects(),
      ]);
      setTasks(t);
      setStaff(s.users || []);
      setProjects(p);
    } catch (e) { toast.error(fmtErr(e, 'Failed to load tasks')); }
    finally { setLoading(false); }
  }, [adminPass]);

  useEffect(() => { refresh(); }, [refresh]);

  // Deep-link from the notification bell: ?task=<id>
  useEffect(() => {
    const id = searchParams.get('task');
    if (!id || loading) return;
    const found = [...tasks.open, ...tasks.done].find(t => t.id === id);
    if (found) {
      handleSelect(found);
      // clear the query param once opened
      const next = new URLSearchParams(searchParams);
      next.delete('task');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tasks, loading]);

  // Deep-link from a project page: ?project=<id> opens the create dialog
  // with that project preselected. Fires once per navigation.
  useEffect(() => {
    if (loading) return;
    if (prefillProjectId && !showCreate && !editing) {
      setShowCreate(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillProjectId, loading]);

  // Group open tasks by priority for display
  const byPriority = useMemo(() => {
    const m = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    tasks.open.forEach(t => { if (m[t.priority]) m[t.priority].push(t); });
    return m;
  }, [tasks.open]);

  const handleSelect = async (task) => {
    setSelected(task);
    if (task.unread_for_admin) {
      try { await adminMarkTaskRead(adminPass, task.id); refresh(); } catch {}
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfaf7]">
      <FurnitureNavBar />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/furniture-projects/staffing')} data-testid="tasks-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl font-bold tracking-tight">Staff Tasks</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Assign and track tasks for each staff member.</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="new-task-btn"><Plus className="h-4 w-4 mr-1.5" />New task</Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-6">
            {/* Open tasks grouped by priority */}
            {[1, 2, 3, 4, 5].map(p => (
              <section key={p} data-testid={`priority-section-${p}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`${PRIORITY_TONE[p]} hover:${PRIORITY_TONE[p]}`}>{PRIORITY_LABEL[p]}</Badge>
                  <span className="text-xs text-muted-foreground">{byPriority[p].length} open</span>
                </div>
                {byPriority[p].length === 0 ? (
                  <p className="text-xs text-muted-foreground italic ml-1">None.</p>
                ) : (
                  <div className="space-y-2">
                    {byPriority[p].map(t => (
                      <TaskRow key={t.id} task={t} onSelect={handleSelect} onEdit={setEditing} />
                    ))}
                  </div>
                )}
              </section>
            ))}

            {/* Done */}
            {tasks.done.length > 0 && (
              <section data-testid="done-section">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Done ({tasks.done.length})</p>
                <div className="space-y-2 opacity-75">
                  {tasks.done.map(t => <TaskRow key={t.id} task={t} onSelect={handleSelect} onEdit={setEditing} />)}
                </div>
              </section>
            )}
          </div>
        )}

        <TaskFormDialog
          open={showCreate}
          onOpenChange={(v) => {
            setShowCreate(v);
            if (!v && prefillProjectId) {
              // Drop the prefill param so reopening the dialog isn't sticky.
              const next = new URLSearchParams(searchParams);
              next.delete('project');
              setSearchParams(next, { replace: true });
            }
          }}
          adminPass={adminPass}
          staff={staff}
          projects={projects}
          prefillProjectId={prefillProjectId}
          onSaved={refresh}
        />
        <TaskFormDialog
          open={!!editing}
          task={editing}
          onOpenChange={(v) => { if (!v) setEditing(null); }}
          adminPass={adminPass}
          staff={staff}
          projects={projects}
          onSaved={refresh}
        />
        <TaskDrawer
          task={selected}
          onClose={() => setSelected(null)}
          adminPass={adminPass}
          isAdmin
          onRefresh={refresh}
        />
      </div>
    </div>
  );
}

function TaskRow({ task, onSelect, onEdit }) {
  const dateLine = priorityDateLine(task);
  return (
    <Card
      className={`cursor-pointer hover:shadow-sm transition-shadow ${task.unread_for_admin ? 'ring-1 ring-emerald-300' : ''}`}
      data-testid={`task-row-${task.id}`}
    >
      <div className="flex items-center gap-3 px-4 py-3" onClick={() => onSelect(task)}>
        {task.status === 'done'
          ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
          : <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${task.status === 'done' ? 'line-through text-muted-foreground' : 'text-[#1f2933]'}`}>
            {task.title}
          </p>
          <p className="text-[11px] text-muted-foreground truncate">
            {task.assignee_name}
            {task.project_name && <> · {task.project_name}</>}
            {dateLine && <> · {dateLine}</>}
          </p>
        </div>
        {(task.thread?.length > 0) && (
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <MessageSquare className="h-3 w-3" /> {task.thread.length}
          </span>
        )}
        {task.unread_for_admin && <span className="h-2 w-2 bg-emerald-500 rounded-full" title="Unread" />}
        <Button
          size="icon" variant="ghost" className="h-7 w-7"
          onClick={(e) => { e.stopPropagation(); onEdit(task); }}
          data-testid={`task-edit-${task.id}`}
        >
          <Pencil className="h-3 w-3" />
        </Button>
      </div>
    </Card>
  );
}

/* ── Task form (create / edit) ─────────────────────────────────── */
function TaskFormDialog({ open, onOpenChange, task, adminPass, staff, projects, prefillProjectId, onSaved }) {
  const isEdit = !!task;
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (task) setForm(fromTask(task));
      else setForm({ ...blankForm(), project_id: prefillProjectId || '' });
    }
  }, [open, task, prefillProjectId]);

  function blankForm() {
    return {
      title: '', description: '', assignee_id: '', project_id: '',
      priority: 3, due_at: '', due_date: '', start_date: '', end_date: '',
      position: 'bottom',
    };
  }
  function fromTask(t) {
    return {
      title: t.title || '', description: t.description || '',
      assignee_id: t.assignee_id || '', project_id: t.project_id || '',
      priority: t.priority || 3,
      due_at: t.due_at || '', due_date: t.due_date || '',
      start_date: t.start_date || '', end_date: t.end_date || '',
      position: 'bottom',
    };
  }

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!form.assignee_id) { toast.error('Pick an assignee'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description,
        assignee_id: form.assignee_id,
        project_id: form.project_id || null,
        priority: Number(form.priority),
        due_at: form.priority === 2 ? form.due_at : null,
        due_date: form.priority === 3 ? form.due_date : null,
        start_date: form.priority === 4 ? form.start_date : null,
        end_date: form.priority === 4 ? form.end_date : null,
      };
      if (isEdit) {
        await adminUpdateTask(adminPass, task.id, payload);
        toast.success('Task updated');
      } else {
        payload.position = form.position;
        await adminCreateTask(adminPass, payload);
        toast.success('Task created');
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(fmtErr(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!task || !window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await adminDeleteTask(adminPass, task.id);
      toast.success('Task deleted');
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit task' : 'New task'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Update task details.' : 'Assign work to a staff member.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="What needs doing?" className="mt-1" data-testid="task-title-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Optional context" className="mt-1" data-testid="task-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Assignee</Label>
              <Select value={form.assignee_id} onValueChange={v => setForm({ ...form, assignee_id: v })}>
                <SelectTrigger className="mt-1" data-testid="task-assignee-select"><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>
                  {staff.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project (optional)</Label>
              <Select value={form.project_id || 'none'} onValueChange={v => setForm({ ...form, project_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="mt-1" data-testid="task-project-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Standalone (no project)</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={String(form.priority)} onValueChange={v => setForm({ ...form, priority: Number(v) })}>
              <SelectTrigger className="mt-1" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 · ASAP (highest)</SelectItem>
                <SelectItem value="2">2 · By deadline (date + time)</SelectItem>
                <SelectItem value="3">3 · On a specific date</SelectItem>
                <SelectItem value="4">4 · Within a date range</SelectItem>
                <SelectItem value="5">5 · When time allows (lowest)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.priority === 2 && (
            <div>
              <Label>Due (date &amp; time)</Label>
              <Input type="datetime-local" value={form.due_at?.slice(0, 16) || ''} onChange={e => setForm({ ...form, due_at: e.target.value })} className="mt-1" data-testid="task-due-at-input" />
            </div>
          )}
          {form.priority === 3 && (
            <div>
              <Label>Do on date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="mt-1" data-testid="task-due-date-input" />
            </div>
          )}
          {form.priority === 4 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="task-start-input" />
              </div>
              <div>
                <Label>End</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="task-end-input" />
              </div>
            </div>
          )}
          {!isEdit && (
            <div>
              <Label>Place in queue</Label>
              <Select value={form.position} onValueChange={v => setForm({ ...form, position: v })}>
                <SelectTrigger className="mt-1" data-testid="task-position-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bottom">At the end (after existing P{form.priority} tasks)</SelectItem>
                  <SelectItem value="top">At the top (before existing P{form.priority} tasks)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          {isEdit && <Button variant="outline" className="text-destructive border-destructive/40" onClick={del} data-testid="task-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="task-save-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Drawer: task detail + thread (shared with staff view via prop) ── */
export function TaskDrawer({ task, onClose, adminPass, isAdmin, onRefresh, postMessageFn, navigateToProject }) {
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);

  if (!task) return null;
  const dateLine = priorityDateLine(task);

  const send = async () => {
    if (!reply.trim()) return;
    setPosting(true);
    try {
      if (isAdmin) await adminPostTaskMessage(adminPass, task.id, reply.trim());
      else if (postMessageFn) await postMessageFn(task.id, reply.trim());
      setReply('');
      onRefresh && onRefresh();
      toast.success('Sent');
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setPosting(false); }
  };

  return (
    <Dialog open={!!task} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {task.status === 'done'
              ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              : <AlertOctagon className="h-5 w-5 text-amber-600" />}
            {task.title}
          </DialogTitle>
          <DialogDescription>
            <span className="flex flex-wrap items-center gap-2 text-xs">
              <Badge className={PRIORITY_TONE[task.priority]}>{PRIORITY_LABEL[task.priority]}</Badge>
              {task.assignee_name && <span>· {task.assignee_name}</span>}
              {task.project_name && (
                navigateToProject ? (
                  <button onClick={() => navigateToProject(task.project_id)} className="text-emerald-700 hover:underline" data-testid="task-open-project">
                    · {task.project_name}
                  </button>
                ) : <span>· {task.project_name}</span>
              )}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {dateLine && <p className="text-xs text-[#1f2933] flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {dateLine}</p>}
          {task.description && (
            <div className="rounded-md bg-muted/40 p-3 text-sm whitespace-pre-wrap">{task.description}</div>
          )}
          {task.status === 'done' && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 space-y-1">
              <p className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3" />Marked done</p>
              <p className="text-[11px] text-emerald-700">{fmtDateTime(task.done_at)}</p>
              {task.done_note && <p className="text-xs text-[#1f2933] whitespace-pre-wrap pt-1 border-t border-emerald-200">{task.done_note}</p>}
            </div>
          )}

          {/* Thread */}
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Messages</p>
            {(task.thread || []).length === 0 && (
              <p className="text-xs text-muted-foreground italic">No messages yet.</p>
            )}
            {(task.thread || []).map(m => (
              <div key={m.id} className={`rounded-md p-2 text-sm ${m.author_role === 'admin' ? 'bg-emerald-50 border border-emerald-100' : 'bg-blue-50 border border-blue-100'}`} data-testid={`thread-msg-${m.id}`}>
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-[11px] font-medium">{m.author_name} <span className="opacity-60">({m.author_role})</span></span>
                  <span className="text-[10px] text-muted-foreground">{fmtDateTime(m.created_at)}</span>
                </div>
                <p className="whitespace-pre-wrap">{m.body}</p>
              </div>
            ))}
          </div>

          {/* Reply */}
          <div className="flex items-end gap-2 pt-2 border-t">
            <Textarea
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder="Reply…"
              rows={2}
              className="flex-1 text-sm"
              data-testid="task-reply-input"
            />
            <Button size="sm" onClick={send} disabled={posting || !reply.trim()} data-testid="task-reply-send">
              {posting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
